import { ALL_PERMISSIONS, FIXED_LIMITS, Permissions, toBitfield, type ChannelDto, type ChannelType, type PermissionOverwriteDto } from '@lagerfeuer/shared';
import { channelPermissions, effectiveOverwrites } from '../../store/selectors';
import type { AppData } from '../../store/types';
import { actorFor } from '../server-settings/hierarchy';

/** Textartige Kanalnamen: klein, Leerzeichen → „-" (wie in der Kanalliste üblich). */
export function normalizeChannelName(name: string, type: ChannelType): string {
  const trimmed = name.slice(0, FIXED_LIMITS.channelNameMax);
  if (type === 'VOICE' || type === 'CATEGORY') return trimmed;
  return trimmed.toLowerCase().replace(/\s+/g, '-').replace(/-{2,}/g, '-');
}

export type TriState = 'allow' | 'neutral' | 'deny';

export function triState(o: Pick<PermissionOverwriteDto, 'allow' | 'deny'> | undefined, bit: bigint): TriState {
  if (!o) return 'neutral';
  if ((toBitfield(o.allow) & bit) === bit) return 'allow';
  if ((toBitfield(o.deny) & bit) === bit) return 'deny';
  return 'neutral';
}

/** Setzt ein Bit in allow/deny gemäß Zustand. */
export function applyTriState(o: { allow: bigint; deny: bigint }, bit: bigint, state: TriState): { allow: bigint; deny: bigint } {
  const allow = state === 'allow' ? o.allow | bit : o.allow & ~bit;
  const deny = state === 'deny' ? o.deny | bit : o.deny & ~bit;
  return { allow, deny };
}

/**
 * Bits, die man in Kanal-Overrides setzen darf: Owner/ADMINISTRATOR alles, sonst die eigenen Rechte im Kanal
 * (Vertrag 2.2: „nur eigene Rechte vergeben").
 */
export function grantableInChannel(s: AppData, ch: ChannelDto): bigint {
  if (!ch.serverId) return 0n;
  const actor = actorFor(s, ch.serverId);
  if (!actor) return 0n;
  if (actor.isOwner || actor.isAdmin) return ALL_PERMISSIONS;
  return channelPermissions(s, ch.id);
}

/** Overrides nach einer Änderung lokal übernehmen (synchronisierte Kanäle kopieren zuerst die Kategorie, D16). */
export function withOverwrite(s: AppData, ch: ChannelDto, next: PermissionOverwriteDto | { targetType: PermissionOverwriteDto['targetType']; targetId: string; remove: true }): ChannelDto {
  const base = [...effectiveOverwrites(s, ch)];
  const rest = base.filter((o) => !(o.targetType === next.targetType && o.targetId === next.targetId));
  const list = 'remove' in next ? rest : [...rest, next];
  return { ...ch, permissionOverwrites: list, permissionsSynced: false };
}

export const SLOWMODES: { value: number; label: string }[] = [
  { value: 0, label: 'Aus' },
  { value: 5, label: '5 Sekunden' },
  { value: 10, label: '10 Sekunden' },
  { value: 15, label: '15 Sekunden' },
  { value: 30, label: '30 Sekunden' },
  { value: 60, label: '1 Minute' },
  { value: 120, label: '2 Minuten' },
  { value: 300, label: '5 Minuten' },
  { value: 600, label: '10 Minuten' },
  { value: 900, label: '15 Minuten' },
  { value: 1800, label: '30 Minuten' },
  { value: 3600, label: '1 Stunde' },
  { value: 7200, label: '2 Stunden' },
  { value: 21600, label: '6 Stunden' },
];

/** Zugang für „privat": @everyone darf den Kanal nicht sehen. */
export function isPrivate(ch: ChannelDto, overwrites: PermissionOverwriteDto[]): boolean {
  const ev = overwrites.find((o) => o.targetType === 'ROLE' && o.targetId === ch.serverId);
  return !!ev && (toBitfield(ev.deny) & Permissions.VIEW_CHANNEL) !== 0n;
}
